import { AppComponent } from './app.component';
import { isDevMode, NgModule } from '@angular/core';
import { environment } from '../environments/environment';
import { appRouteProviders, appRoutes } from './app.routes';
import {
    AppModuleConfiguration,
    SharedUiAppSharedModule,
} from '@pf/shared/ui-app-shared';
import loadProviderConfig from './config/load-provider-config';
import globalConfig from './config/global-config';
import appBranding from './config/app-branding';
import appConstants from './config/app-constants';
import { PreloadAllModules, RouterModule } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
    IconModule,
    LayoutModule,
    NotificationsModule,
    SharedUiModule,
} from '@pf/shared-ui';
import { AuthModule } from '@pf/shared-services';
import { PlatformCoreDomainModule } from '@pf/platform-core/domain';
import {
    ITEM_CATALOG_KIT_BOM_ENABLED,
    ITEM_CRITICAL_CUSTOM_FIELD_KEY,
    ITEM_HAZARD_CODE_CUSTOM_FIELD_KEY,
    PlatformItemCatalogDomainModule,
} from '@pf/platform-item-catalog/domain';
import { PlatformAuthService, sharedEnv } from '@pf/shared-common';
import { lastValueFrom, tap } from 'rxjs';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CpwAppHeaderComponent } from '@cpw/ui-shared';
import { PlatformWmsDomainModule } from '@pf/platform-wms/domain';
import { coreProviderConfig } from './config/core-provider-config';
import {
    CPW_BFF_URL,
    CycleCountPositionExitCleanupTask,
    EquipmentPositionExitCleanupTask,
    PackingPositionExitCleanupTask,
    PickingPositionExitCleanupTask,
    PutAwayPositionExitCleanupTask,
    ReplenishmentsPositionExitCleanupTask,
} from '@cpw/domain';
import { USER_POSITION_EXIT_CLEANUP_TASKS } from '@pf/platform-core/domain';
import { PlatformOrdersDomainModule } from '@pf/platform-orders/domain';
import { HttpClientModule } from '@angular/common/http';

const appModuleConfiguration = new AppModuleConfiguration(environment)
    .withRoutes(appRoutes, appRouteProviders)
    .withAppBranding(appBranding)
    .withAppTenantId('2d33e10d-612a-4b30-9aed-b895868145b9')
    .withGlobalConfig(globalConfig)
    .withAppConstants(appConstants)
    .withCoreProviderConfig(coreProviderConfig)
    .withLoadProviderConfig(loadProviderConfig);

export function jwtOptionsFactory(tokenService: PlatformAuthService) {
    return {
        tokenGetter: () => {
            return lastValueFrom(
                tokenService.getAccessToken().pipe(
                    tap(token => {
                        if (!token) {
                            tokenService.login();
                        }
                    })
                )
            );
        },
        allowedDomains: sharedEnv.jwtConfig.allowedList,
        disallowedRoutes: sharedEnv.jwtConfig.disallowedList,
    };
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        FormsModule,
        BrowserAnimationsModule,
        IconModule,
        SharedUiModule,
        LayoutModule.forRoot(
            appModuleConfiguration.environment.mainUrl,
            environment.environment !== 'prod'
        ),
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: [PlatformAuthService],
            },
        }),
        AuthModule.forRoot(
            appModuleConfiguration.environment.auth0ClientId,
            appModuleConfiguration.environment.auth0AuthParams
        ),
        PlatformCoreDomainModule.ApiModule(),
        PlatformCoreDomainModule,
        PlatformItemCatalogDomainModule.ApiModule(),
        PlatformItemCatalogDomainModule,
        PlatformWmsDomainModule.ApiModule(),
        PlatformWmsDomainModule,
        PlatformOrdersDomainModule.ApiModule(),
        NotificationsModule.forRoot(),
        SharedUiAppSharedModule,
        RouterModule.forRoot(appModuleConfiguration.appRoutes, {
            preloadingStrategy: PreloadAllModules,
        }),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: !isDevMode(),
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000',
        }),
        CpwAppHeaderComponent,
        HttpClientModule,
    ],
    providers: [
        ...appModuleConfiguration.providers,
        { provide: CPW_BFF_URL, useValue: environment.bffUrl },
        // Item catalog UI tokens: see layering notes on `item-catalog-ui.tokens` in @pf/platform-item-catalog/domain.
        {
            provide: ITEM_CRITICAL_CUSTOM_FIELD_KEY,
            useValue: 'Critical',
        },
        {
            provide: ITEM_HAZARD_CODE_CUSTOM_FIELD_KEY,
            useValue: 'Hazard Code',
        },
        {
            provide: ITEM_CATALOG_KIT_BOM_ENABLED,
            useValue: true,
        },
        {
            provide: USER_POSITION_EXIT_CLEANUP_TASKS,
            useClass: PickingPositionExitCleanupTask,
            multi: true,
        },
        {
            provide: USER_POSITION_EXIT_CLEANUP_TASKS,
            useClass: PackingPositionExitCleanupTask,
            multi: true,
        },
        {
            provide: USER_POSITION_EXIT_CLEANUP_TASKS,
            useClass: CycleCountPositionExitCleanupTask,
            multi: true,
        },
        {
            provide: USER_POSITION_EXIT_CLEANUP_TASKS,
            useClass: ReplenishmentsPositionExitCleanupTask,
            multi: true,
        },
        {
            provide: USER_POSITION_EXIT_CLEANUP_TASKS,
            useClass: PutAwayPositionExitCleanupTask,
            multi: true,
        },
        {
            provide: USER_POSITION_EXIT_CLEANUP_TASKS,
            useClass: EquipmentPositionExitCleanupTask,
            multi: true,
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
